import {
  addDisplayName,
  addEffect,
  addHandlers,
  addProps,
  addRef,
  addState,
  addStateHandlers,
  addWrapper,
  flowMax,
} from 'ad-hok'
import {
  AgGridEvent,
  ColDef,
  RowClickedEvent,
  RowSelectedEvent,
  ValueFormatterParams,
} from 'ag-grid-community'
import { AgGridReact } from 'ag-grid-react'
import cx from 'classnames'
import saveAs from 'file-saver'
import React, { ChangeEvent, FC } from 'react'
import { createUseStyles } from 'react-jss'
import { addLocation, addNavigate } from 'shared/src/utils/addNavigate'
import { addDataLoadingFromPromise } from 'shared/src/utils/dataLoading'
import { typedAs } from 'shared/src/utils/typedAs'

import 'ag-grid-community/styles/ag-grid.css'
import 'ag-grid-community/styles/ag-theme-alpine.css'
import { addTranslationHelpers } from 'utils/addTranslationHelpers'
import { addCmsLoadingAndErrorIndicators } from '../utils/addCmsLoadingAndErrorIndicators'
import { addResource } from '../utils/addResource'
import { Button } from './Button'
import { DateRange, getToday } from './DateRange'
import { Page } from './Page'

export const DEFAULT_COL_DEF: ColDef = {
  filter: 'agTextColumnFilter',
  sortable: true,
  resizable: true,
}

export const DATE_COLUMN: ColDef = {
  filter: 'agDateColumnFilter',
  valueFormatter: (params: ValueFormatterParams<Date>) =>
    params.value?.toLocaleString(undefined, { dateStyle: 'medium' }),
}

export const NUMBER_COLUMN: ColDef = {
  filter: 'agNumberColumnFilter',
  headerClass: 'ag-right-aligned-header',
  cellClass: 'ag-right-aligned-cell',
  width: 120,
}

export const COLUMN_TYPES = {
  date: DATE_COLUMN,
  number: NUMBER_COLUMN,
}

const useStyles = createUseStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    height: 'calc(100dvh - 110px)',
  },
  table: {
    marginTop: 20,
    width: '100%',
    flexGrow: 1,
  },
  buttons: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  gridActionsContainer: {
    display: 'flex',
    gap: 20,
    marginTop: 20,
  },
  quickFilter: {
    width: 200,
    backgroundColor: 'rgba(54,52,50,.02)',
    color: 'rgba(54,52,50,.96)',
    borderColor: 'rgb(144,115,79,.24)',
    borderRadius: 0.125,
    borderWidth: 1,
    fontSize: 16,
    paddingTop: 10,
    paddingBottom: 10,
    paddingLeft: 4,
    '&:focus': {
      backgroundColor: 'rgba(54, 52, 50, 0.04)',
      borderColor: 'rgba(144, 115, 79, 0.56)',
      outline: 'none',
    },
  },
})

const EARLIEST_DATE = new Date(2022, 0, 1)

export const ResourceIndex: FC = flowMax(
  addDisplayName('ResourceIndex'),
  addResource,
  addWrapper((render, { resourceName }) => (
    <React.Fragment key={resourceName}>
      <Page>{render()}</Page>
    </React.Fragment>
  )),
  addState('dateRange', 'setDateRange', [EARLIEST_DATE, getToday()]),
  addState('includeStats', 'setIncludeStats', false),
  addDataLoadingFromPromise(
    'values',
    ({ resource: { api }, dateRange: [startDate, endDate], includeStats }) =>
      api.getAllWithDateRange
        ? api.getAllWithDateRange(startDate, endDate, includeStats)
        : api.getAll(),
    ['resource', 'dateRange', 'includeStats']
  ),
  addCmsLoadingAndErrorIndicators('values'),
  addRef('gridRef', typedAs<AgGridReact | null>(null)),
  addStateHandlers(
    {
      checkedIds: new Set<number>(),
    },
    {
      toggleId:
        ({ checkedIds }) =>
        (event: RowSelectedEvent) => {
          const id = event?.data?.id

          if (!id) {
            return { checkedIds }
          }

          const newCheckedIds = new Set(checkedIds)

          if (checkedIds.has(id)) {
            newCheckedIds.delete(id)
          } else {
            newCheckedIds.add(id)
          }

          return { checkedIds: newCheckedIds }
        },
    }
  ),
  addState('isDeleting', 'setDeleting', false),
  addState('filteredValues', 'setFilteredValues', ({ values }) => values),
  addNavigate,
  addTranslationHelpers,
  addLocation,
  addHandlers({
    onCreate:
      ({ navigate }) =>
      () => {
        navigate('create')
      },
    onDelete:
      ({
        resource: { api, pluralModelName },
        checkedIds,
        setDeleting,
        setValues,
        t,
      }) =>
      () => {
        if (
          !window.confirm(
            t('admin.resources.general.deleteConfirmation', {
              count: checkedIds.size,
              name: pluralModelName.toLowerCase(),
            })
          )
        ) {
          return
        }

        setDeleting(true)
        Promise.all(Array.from(checkedIds).map(api.delete))
          .then(() => api.getAll())
          .then(setValues)
          .catch((error) => {
            console.error(error)
          })
          .finally(() => {
            setDeleting(false)
          })
      },
    onSelect:
      ({ location, filteredValues, resourceName }) =>
      (event: RowClickedEvent) => {
        const data = event.data

        if (!data) {
          return
        }

        const ids = filteredValues.map(({ id }) => id)
        location.state = { ids }
        localStorage.setItem('ids', JSON.stringify(ids))
        window.open(`${resourceName}/${data.id.toString()}`, '_blank')
      },
    onFilterOrSortChanged:
      ({ setFilteredValues }) =>
      (event: AgGridEvent) => {
        const filteredValues: any[] = []

        event.api.forEachNodeAfterFilterAndSort((row) =>
          filteredValues.push(row.data)
        )

        setFilteredValues(filteredValues)
      },
    onChangeQuickFilter:
      ({ gridRef }) =>
      (event: ChangeEvent<HTMLInputElement>) => {
        gridRef.current!.api.setQuickFilter(event.target.value)
      },
    onAgGridExport:
      ({ gridRef, resource: { pluralModelName } }) =>
      () => {
        gridRef.current!.api.exportDataAsCsv({ fileName: pluralModelName })
      },
  }),
  addEffect(
    ({ gridRef, setFilteredValues }) =>
      () => {
        const filteredValues: any[] = []

        gridRef.current?.api?.forEachNodeAfterFilterAndSort((row) =>
          filteredValues.push(row.data)
        )

        if (filteredValues.length !== 0) {
          setFilteredValues(filteredValues)
        }
      },
    ['values']
  ),
  addProps(
    ({
      resource: {
        pluralModelName,
        api: { exportCsv },
      },
    }) => ({
      onExport: exportCsv
        ? async () => {
            const response = await exportCsv()
            const blob = await response.blob()
            saveAs(blob, `${pluralModelName}.csv`)
          }
        : undefined,
    })
  ),
  addProps(({ resource: { canDelete } }) => ({
    canDelete: canDelete ?? true,
  })),
  addProps(
    ({ resource: { columns } }) => ({
      columns: columns.map((column, index) =>
        index === 0 ? { ...column, checkboxSelection: true } : column
      ),
    }),
    ['resource']
  ),
  addProps(
    ({
      resource: {
        api: { getAllWithDateRange },
      },
    }) => ({
      showDatePickers: !!getAllWithDateRange,
    })
  ),
  addProps(() => ({
    classes: useStyles(),
  })),
  ({
    resource: { pluralModelName, IndexExtraButtons, IndexExtraHeader },
    showDatePickers,
    dateRange: [startDate, endDate],
    setDateRange,
    includeStats,
    setIncludeStats,
    gridRef,
    columns,
    canDelete,
    values,
    reloadValues,
    checkedIds,
    toggleId,
    onCreate,
    isDeleting,
    onDelete,
    onExport,
    onSelect,
    onFilterOrSortChanged,
    onChangeQuickFilter,
    onAgGridExport,
    t,
    classes,
  }) => (
    <div className={classes.container}>
      <h1>{pluralModelName}</h1>
      {showDatePickers && (
        <DateRange
          startDate={startDate}
          endDate={endDate}
          onChange={(startDate, endDate) => {
            setDateRange([startDate, endDate])
          }}
        />
      )}
      <div className={classes.buttons}>
        <Button text={t('admin.resources.general.create')} onClick={onCreate} />
        {canDelete && (
          <Button
            text={t('admin.resources.general.delete')}
            disabled={isDeleting || checkedIds.size === 0}
            onClick={onDelete}
          />
        )}
        {onExport && (
          <Button
            text={t('admin.resources.products.exportCsv')}
            onClick={onExport}
          />
        )}
        {IndexExtraButtons && <IndexExtraButtons reload={reloadValues} />}
        {showDatePickers && (
          <>
            <input
              type="checkbox"
              checked={includeStats}
              onChange={(event) => setIncludeStats(event.target.checked)}
            />
            {t('admin.resources.general.includeStats')}
          </>
        )}
      </div>
      {IndexExtraHeader && <IndexExtraHeader />}
      <div className={classes.gridActionsContainer}>
        <input
          onChange={onChangeQuickFilter}
          placeholder={t('admin.resources.general.filter')}
          className={classes.quickFilter}
        />
        <Button
          text={t('admin.resources.general.agGridExport')}
          onClick={onAgGridExport}
        />
      </div>
      <div className={cx('ag-theme-alpine', classes.table)}>
        <AgGridReact
          ref={gridRef}
          rowSelection="multiple"
          columnDefs={columns}
          columnTypes={COLUMN_TYPES}
          defaultColDef={DEFAULT_COL_DEF}
          rowData={values}
          onRowDoubleClicked={onSelect}
          onRowSelected={toggleId}
          onFilterChanged={onFilterOrSortChanged}
          onSortChanged={onFilterOrSortChanged}
          cacheQuickFilter={true}
        />
      </div>
    </div>
  )
)
